import React, { createContext, useContext, useState } from 'react'

interface BetaModeContextValue {
  betaMode: boolean
  setBetaMode: (value: boolean) => void
}

const BetaModeContext = createContext<BetaModeContextValue | undefined>(undefined)

export function BetaModeProvider({ children }: { children: React.ReactNode }) {
  const getStoredBetaMode = () => {
    if (typeof window === 'undefined') return false
    try {
      return localStorage.getItem('betaMode') === 'true'
    } catch (error) {
      console.warn('Failed to read beta mode from localStorage:', error)
      return false
    }
  }

  const defaultMode =
    import.meta.env.VITE_BETA_MODE === 'true' || getStoredBetaMode() || true // Enable by default for demo

  const [betaMode, setBetaModeState] = useState<boolean>(defaultMode)

  const setBetaMode = (value: boolean) => {
    setBetaModeState(value)
    if (typeof window !== 'undefined') {
      localStorage.setItem('betaMode', value ? 'true' : 'false')
    }
  }

  return (
    <BetaModeContext.Provider value={{ betaMode, setBetaMode }}>
      {children}
    </BetaModeContext.Provider>
  )
}

export function useBetaMode(): boolean {
  const context = useContext(BetaModeContext)
  if (!context) {
    throw new Error('useBetaMode must be used within a BetaModeProvider')
  }
  return context.betaMode
}

export function useSetBetaMode(): (value: boolean) => void {
  const context = useContext(BetaModeContext)
  if (!context) {
    throw new Error('useSetBetaMode must be used within a BetaModeProvider')
  }
  return context.setBetaMode
}

/**
 * Alias providing the same behavior as `useSetBetaMode` but named without the
 * "use" prefix. Some components expect a hook called `setBetaMode` so this is
 * exported for convenience.
 * 
 * Note: This is still a React hook and must be called within a React component.
 */
export const setBetaMode = useSetBetaMode
